import type { MeasurementSystem } from '@/shared/domain'

const KPA_PER_INHG = 3.38639
const MM_PER_INCH = 25.4
const CM_PER_INCH = 2.54
const METERS_PER_YARD = 0.9144
const METERS_PER_FOOT = 0.3048
const KM_PER_MILE = 1.609344
const KMH_PER_MPH = 1.609344
const KG_PER_POUND = 0.45359237
const HECTARES_PER_ACRE = 0.404686
const CUBIC_METERS_PER_CUBIC_FOOT = 0.028316846592
const METERS_PER_MILE = 1609.344
const INCHES_PER_FOOT = 12

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

function fixed(value: number, decimals: number): string {
  return roundTo(value, decimals).toFixed(decimals)
}

function isMetric(system: MeasurementSystem): boolean {
  return system === 'metric'
}

export function convertTemperature(fahrenheit: number, system: MeasurementSystem): number {
  return isMetric(system) ? ((fahrenheit - 32) * 5) / 9 : fahrenheit
}

export function temperatureUnit(system: MeasurementSystem): string {
  return isMetric(system) ? '°C' : '°F'
}

export function formatTemperatureValue(fahrenheit: number, system: MeasurementSystem): string {
  return Math.round(convertTemperature(fahrenheit, system)).toString()
}

export function formatTemperature(fahrenheit: number, system: MeasurementSystem): string {
  return `${formatTemperatureValue(fahrenheit, system)}${temperatureUnit(system)}`
}

export function temperatureToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo((value * 9) / 5 + 32, 1) : value
}

export function convertWindSpeed(mph: number, system: MeasurementSystem): number {
  return isMetric(system) ? mph * KMH_PER_MPH : mph
}

export function windSpeedUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'km/h' : 'mph'
}

export function formatWindSpeedValue(mph: number, system: MeasurementSystem): string {
  return Math.round(convertWindSpeed(mph, system)).toString()
}

export function formatWindSpeed(mph: number, system: MeasurementSystem): string {
  return `${formatWindSpeedValue(mph, system)} ${windSpeedUnit(system)}`
}

export function windSpeedToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / KMH_PER_MPH, 1) : value
}

export function convertPressure(inHg: number, system: MeasurementSystem): number {
  return isMetric(system) ? inHg * KPA_PER_INHG : inHg
}

export function pressureUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'kPa' : 'inHg'
}

export function formatPressureValue(inHg: number, system: MeasurementSystem): string {
  return isMetric(system) ? fixed(convertPressure(inHg, system), 1) : fixed(inHg, 2)
}

export function formatPressure(inHg: number, system: MeasurementSystem): string {
  return `${formatPressureValue(inHg, system)} ${pressureUnit(system)}`
}

export function pressureToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / KPA_PER_INHG, 2) : value
}

export function convertPrecipitation(inches: number, system: MeasurementSystem): number {
  return isMetric(system) ? inches * MM_PER_INCH : inches
}

export function precipitationUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'mm' : 'in'
}

export function formatPrecipitationValue(inches: number, system: MeasurementSystem): string {
  return isMetric(system) ? fixed(convertPrecipitation(inches, system), 1) : fixed(inches, 2)
}

export function formatPrecipitation(inches: number, system: MeasurementSystem): string {
  return `${formatPrecipitationValue(inches, system)} ${precipitationUnit(system)}`
}

export function precipitationToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / MM_PER_INCH, 2) : value
}

export function convertYards(yards: number, system: MeasurementSystem): number {
  return isMetric(system) ? yards * METERS_PER_YARD : yards
}

export function yardsUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'm' : 'yds'
}

export function formatYardsValue(yards: number, system: MeasurementSystem): string {
  return Math.round(convertYards(yards, system)).toString()
}

export function formatYards(yards: number, system: MeasurementSystem): string {
  return `${formatYardsValue(yards, system)} ${yardsUnit(system)}`
}

export function yardsToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / METERS_PER_YARD, 1) : value
}

export function convertFeet(feet: number, system: MeasurementSystem): number {
  return isMetric(system) ? feet * METERS_PER_FOOT : feet
}

export function feetUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'm' : 'ft'
}

export function formatFeetValue(feet: number, system: MeasurementSystem): string {
  return Math.round(convertFeet(feet, system)).toString()
}

export function formatFeet(feet: number, system: MeasurementSystem): string {
  return `${formatFeetValue(feet, system)} ${feetUnit(system)}`
}

export function feetToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / METERS_PER_FOOT, 1) : value
}

const WATER_LEVEL_DECIMALS = 2

export function convertWaterLevelFeet(feet: number, system: MeasurementSystem): number {
  return convertFeet(feet, system)
}

export function formatWaterLevelValue(
  feet: number,
  system: MeasurementSystem,
  decimals: number = WATER_LEVEL_DECIMALS,
): string {
  return fixed(convertFeet(feet, system), decimals)
}

export function formatWaterLevel(
  feet: number,
  system: MeasurementSystem,
  decimals: number = WATER_LEVEL_DECIMALS,
): string {
  return `${formatWaterLevelValue(feet, system, decimals)} ${feetUnit(system)}`
}

export function convertMiles(miles: number, system: MeasurementSystem): number {
  return isMetric(system) ? miles * KM_PER_MILE : miles
}

export function milesUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'km' : 'mi'
}

export function formatMiles(miles: number, system: MeasurementSystem): string {
  if (!isMetric(system)) return `${fixed(miles, 1)} mi`
  const km = convertMiles(miles, system)
  const roundedMeters = Math.round(km * 1000)
  if (roundedMeters < 1000) return `${roundedMeters} m`
  return `${fixed(km, 1)} km`
}

export function milesToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / KM_PER_MILE, 2) : value
}

export function formatMeters(meters: number, system: MeasurementSystem): string {
  if (isMetric(system)) {
    const roundedMeters = Math.round(meters)
    if (roundedMeters < 1000) return `${roundedMeters} m`
    return `${fixed(meters / 1000, 1)} km`
  }
  return `${fixed(meters / METERS_PER_MILE, 1)} mi`
}

export function convertInches(inches: number, system: MeasurementSystem): number {
  return isMetric(system) ? inches * CM_PER_INCH : inches
}

export function inchesUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'cm' : 'in'
}

export function formatInchesValue(inches: number, system: MeasurementSystem): string {
  return isMetric(system) ? Math.round(convertInches(inches, system)).toString() : fixed(inches, 1)
}

export function formatInches(inches: number, system: MeasurementSystem): string {
  return `${formatInchesValue(inches, system)} ${inchesUnit(system)}`
}

export function inchesToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / CM_PER_INCH, 1) : value
}

export function formatFeetInches(totalInches: number, system: MeasurementSystem): string {
  if (isMetric(system)) return `${fixed((totalInches * CM_PER_INCH) / 100, 2)} m`
  const feet = Math.floor(totalInches / INCHES_PER_FOOT)
  const inches = Math.round(totalInches - feet * INCHES_PER_FOOT)
  return `${feet}' ${inches}"`
}

export function convertPounds(pounds: number, system: MeasurementSystem): number {
  return isMetric(system) ? pounds * KG_PER_POUND : pounds
}

export function weightUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'kg' : 'lbs'
}

export function formatWeightValue(pounds: number, system: MeasurementSystem): string {
  return isMetric(system) ? fixed(convertPounds(pounds, system), 1) : fixed(pounds, 1)
}

export function formatWeight(pounds: number, system: MeasurementSystem): string {
  return `${formatWeightValue(pounds, system)} ${weightUnit(system)}`
}

export function formatBigGameWeightValue(pounds: number, system: MeasurementSystem): string {
  return Math.round(convertPounds(pounds, system)).toString()
}

export function formatBigGameWeight(pounds: number, system: MeasurementSystem): string {
  return `${formatBigGameWeightValue(pounds, system)} ${weightUnit(system)}`
}

export function poundsToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / KG_PER_POUND, 1) : value
}

export function convertAcres(acres: number, system: MeasurementSystem): number {
  return isMetric(system) ? acres * HECTARES_PER_ACRE : acres
}

export function areaUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'ha' : 'acres'
}

export function formatAreaValue(acres: number, system: MeasurementSystem): string {
  return fixed(convertAcres(acres, system), 1)
}

export function formatArea(acres: number, system: MeasurementSystem): string {
  return `${formatAreaValue(acres, system)} ${areaUnit(system)}`
}

export function acresToImperial(value: number, system: MeasurementSystem): number {
  return isMetric(system) ? roundTo(value / HECTARES_PER_ACRE, 2) : value
}

export function convertFlow(cfs: number, system: MeasurementSystem): number {
  return isMetric(system) ? cfs * CUBIC_METERS_PER_CUBIC_FOOT : cfs
}

export function flowUnit(system: MeasurementSystem): string {
  return isMetric(system) ? 'm³/s' : 'cfs'
}

export function formatFlowValue(cfs: number, system: MeasurementSystem): string {
  if (!isMetric(system)) return Math.round(cfs).toLocaleString('en-US')
  const cms = convertFlow(cfs, system)
  if (cms < 1) return fixed(cms, 2)
  if (cms < 100) return fixed(cms, 1)
  return Math.round(cms).toLocaleString('en-US')
}

export function formatFlow(cfs: number, system: MeasurementSystem): string {
  return `${formatFlowValue(cfs, system)} ${flowUnit(system)}`
}
